import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

// `error` is Error-shaped when it is a native Error, OR an Angular HttpErrorResponse:
// GlobalErrorHandler.handleError(error: Error | HttpErrorResponse) forwards either, and
// HttpErrorResponse only IMPLEMENTS the Error interface -- it extends HttpResponseBase, never the
// native Error class -- so `error instanceof Error` is false for every HTTP failure even though it
// carries a real, readable `.message`. An `instanceof Error`-only guard silently discarded that
// message as "[object Object]" for the single most common real-world input to this handler: every
// uncaught HttpClient failure (found by an ultra-review of the original instanceof-only guard).
function isErrorShaped(error: unknown): error is Error | HttpErrorResponse {
    return error instanceof Error || error instanceof HttpErrorResponse;
}

@Injectable({
    providedIn: 'root'
})
export class ErrorService {

    // `error` is typed `unknown`, not `Error`: GlobalErrorHandler.handleError() forwards whatever
    // Zone.js/Angular caught, which is not guaranteed to be an Error instance (a rejected promise
    // can settle with a boolean, undefined, or a plain object). Reading `.message` unguarded on
    // those crashed with `TypeError: Cannot read properties of undefined (reading 'message')` in
    // production telemetry -- guard with `isErrorShaped` (Error OR HttpErrorResponse) before
    // touching any Error-only field, falling back to `String(error)` for anything else.
    getClientMessage(error: unknown): string {
        if (!navigator.onLine) {
            return 'No Internet Connection';
        }
        if (!isErrorShaped(error)) {
            return String(error);
        }
        return error.message ? error.message : error.toString();
    }

    // Falls back to '' (not String(error)) for a non-Error-shaped value, and for a genuine Error
    // whose engine never populated `.stack` (the field is `string | undefined` even on a real
    // Error) -- both branches must return a string, never undefined, to honour this method's own
    // declared return type.
    getClientStack(error: unknown): string {
        return error instanceof Error ? (error.stack ?? '') : '';
    }

    getServerMessage(error: unknown): string {
        return isErrorShaped(error) ? error.message : String(error);
    }
}
